import { useCallback, useEffect, useState } from "react";

import { ApiError, launcherIcon } from "./api";

/**
 * Icône sur l'écran d'accueil.
 *
 * Sur Android, l'application propose d'épingler le festival sur l'écran
 * d'accueil, avec cette icône : un raccourci qui ouvre directement sur le
 * festival (DESIGN.md §4.2). Réservée aux festivals officiels.
 */
export function LauncherIconEditor({ slug }: { slug: string }) {
  const [state, setState] = useState<{
    official: boolean;
    present: boolean;
    version: string | null;
  } | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setState(await launcherIcon.state(slug));
  }, [slug]);

  useEffect(() => {
    void reload().catch(() => setError("Lecture impossible."));
  }, [reload]);

  if (!state) return null;

  const send = async (file: File) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await launcherIcon.upload(slug, file);
      setMessage("Icône enregistrée. Publiez pour l'envoyer aux téléphones.");
      await reload();
    } catch (cause) {
      const code = cause instanceof ApiError ? cause.code : "";
      setError(
        code === "too_small"
          ? "Image trop petite : il faut au moins 192 pixels de côté (512 conseillés)."
          : code === "unreadable_image"
            ? "Image illisible : envoyez un PNG, un JPEG ou un WebP."
            : code === "file_too_large"
              ? "Fichier trop lourd : 5 Mo au plus."
              : "Envoi impossible pour le moment.",
      );
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await launcherIcon.remove(slug);
      setMessage("Icône retirée. Publiez pour que les téléphones l'apprennent.");
      await reload();
    } catch {
      setError("Retrait impossible pour le moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <h2>Icône sur l'écran d'accueil</h2>
      {!state.official ? (
        <p className="muted">
          Réservée aux festivals officiels : une page communautaire n'emprunte pas
          l'identité d'un festival qui n'a rien demandé.
        </p>
      ) : (
        <>
          <p className="muted">
            Sur Android, les festivaliers peuvent épingler votre festival sur leur écran
            d'accueil, avec cette icône : elle ouvre l'application directement sur lui.
            Envoyez une image carrée d'au moins 192 pixels (512 conseillés). Le
            téléphone la découpe en rond ou en carré arrondi : gardez l'essentiel au
            centre.
          </p>
          {error ? <p className="error">{error}</p> : null}
          {message ? <p className="badge">{message}</p> : null}

          {state.present && state.version ? (
            <div className="row" style={{ gap: 24 }}>
              {/* Two crops a launcher may apply: what the organiser checks. */}
              <img
                src={launcherIcon.imageUrl(slug, state.version)}
                alt="Aperçu de l'icône, découpe ronde"
                width={96}
                height={96}
                style={{ borderRadius: "50%" }}
              />
              <img
                src={launcherIcon.imageUrl(slug, state.version)}
                alt="Aperçu de l'icône, découpe en carré arrondi"
                width={96}
                height={96}
                style={{ borderRadius: 22 }}
              />
            </div>
          ) : null}

          <div className="row">
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              disabled={busy}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void send(file);
                event.target.value = "";
              }}
            />
            {state.present ? (
              <button className="danger" disabled={busy} onClick={() => void remove()}>
                Retirer l'icône
              </button>
            ) : null}
          </div>
        </>
      )}
    </section>
  );
}
